import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Guide } from "../api/types";
import { GuideTabs } from "../components/GuideTabs";
import { Icon } from "../components/icons";
import { ReRecordingGuide } from "../components/ReRecordingGuide";
import { toast } from "../components/toast";

/**
 * The owner's Re-recording guide (`/projects/:id/guide`) — the same view the
 * share link shows, plus the switch that puts it on the share link. Always
 * available to the owner; the link carries it only when switched on.
 */
export function ProjectGuide() {
  const { projectId } = useParams();
  const base = `/api/modules/proofing/projects/${projectId}`;
  const key = ["guide", projectId];
  return (
    <div className="pickups-standalone rrg-page">
      <header className="pk-topbar">
        <a className="pk-brand" href={`/projects/${projectId}`} title="Back to the project">
          <Icon name="logo" size={22} />
          <span className="pk-brand-name">Mockingbird</span>
        </a>
        <GuideTabs active="guide" pickupsTo={`/projects/${projectId}/pickups`} guideTo={`/projects/${projectId}/guide`} />
      </header>
      <div className="page rrg-wrap">
        <ReRecordingGuide
          source={{ base, queryKey: key, anonymous: false }}
          ownerBar={(g) => <OwnerBar g={g} base={base} queryKey={key} projectId={projectId!} />}
        />
      </div>
    </div>
  );
}

function OwnerBar({ g, base, queryKey, projectId }: { g: Guide; base: string; queryKey: unknown[]; projectId: string }) {
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const enable = useMutation({
    mutationFn: (enabled: boolean) => api<{ guide_enabled: boolean }>(`${base}/guide/enabled`, {
      method: "PUT", body: JSON.stringify({ enabled }),
    }),
    onSuccess: (r) => qc.setQueryData<Guide>(queryKey, (cur) => cur && { ...cur, guide_enabled: r.guide_enabled }),
    onError: () => toast("That didn’t save. Please try again.", "error"),
  });
  const rebuild = useMutation({
    mutationFn: () => api(`${base}/guide/rebuild`, { method: "POST" }),
    onSuccess: () => { toast("Rebuilding the guide…", "info"); qc.invalidateQueries({ queryKey }); },
  });
  const link = g.share_token ? `${window.location.origin}/share/${g.share_token}/guide` : "";

  return (
    <div className="panel rrg-owner">
      {g.share_enabled ? (
        <>
          <label className="rrg-switch">
            <input type="checkbox" checked={!!g.guide_enabled} disabled={enable.isPending}
              onChange={(e) => enable.mutate(e.target.checked)} />
            <span className="rrg-switch-ui" aria-hidden="true" />
            <span><b>On the share link</b> — people with the Pickups Package link also get this guide.</span>
          </label>
          {g.guide_enabled && link && (
            <span className="rrg-link">
              <span className="mono">{link.replace(/^https?:\/\//, "")}</span>
              <button type="button" className="btn sm" onClick={async () => {
                try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1600); }
                catch { toast("Couldn’t copy — select the link and copy it.", "error"); }
              }}><Icon name="copy" size={14} /> {copied ? "Copied" : "Copy link"}</button>
            </span>
          )}
        </>
      ) : (
        <span className="rrg-owner-note">
          Only you can see this guide. To share it, turn on the share link in the{" "}
          <a href={`/projects/${projectId}/pickups`}>Pickups Package</a>, then switch the guide on here.
        </span>
      )}
      <button type="button" className="rrg-linkbtn rrg-rebuild" onClick={() => rebuild.mutate()} disabled={rebuild.isPending}
        title="Build the guide again from the current pickups">Rebuild</button>
    </div>
  );
}
